"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.watchSUMO1SchedulerSaga = void 0;
const constants_1 = require("@/app/shared/constants");
const helpers_1 = require("@/app/shared/helpers");
const analyticsService_1 = require("@/app/shared/services/analyticsService");
const global_1 = require("@/app/shared/store/global");
const notifications_1 = require("@/app/shared/store/notifications");
const reduxUtils_1 = require("@/app/shared/store/reduxUtils");
const server_1 = require("@/app/SUMO/server");
const effects_1 = require("redux-saga/effects");
const actions_1 = require("./actions");
const sagaUtils_1 = require("./sagaUtils");
const selectors_1 = require("./selectors");
const utils_1 = require("./utils");
function* getSchedulerDataSaga(action) {
    if (action.type !== actions_1.SUMO1SchedulerActionTypes.GET_SCHEDULER_DATA_REQUEST)
        return;
    try {
        const userDataResponse = yield (0, effects_1.call)(server_1.SumoServer.getUserData);
        const { userData } = userDataResponse;
        const profile = userData?.[0];
        if (!profile)
            throw new Error('Profile not found!');
        const tenantId = profile.tenantId;
        const requestsList = [
            (0, effects_1.call)(server_1.SumoServer.getCurrentRole),
            (0, effects_1.call)(server_1.SumoServer.getLicenses, { tenantId }),
            (0, effects_1.call)(server_1.SumoServer.getAdminDataByTenant, { tenantId }),
            (0, effects_1.call)(server_1.SumoServer.getUserDataByTenant, { tenantId }),
            (0, effects_1.call)(server_1.SumoServer.getWorkspaces),
        ];
        const [roleResponse, licensesResponse, adminDataByTenantResponse, userDataByTenantResponse, workspacesResponse] = yield (0, effects_1.all)(requestsList);
        const users = adminDataByTenantResponse.users
            .filter((user) => user.status === "ACTIVE" /* UserStatus.ACTIVE */)
            .map((user) => {
            const userData = userDataByTenantResponse.userData.find((profile) => profile.userId === user.userId);
            return {
                id: user.userId,
                type: "SUMO1" /* MemberType.SUMO1 */,
                subType: "USER" /* MemberSubType.USER */,
                name: userData?.fullName,
                email: user.email,
                status: user.status,
                defaultVideoIntegration: userData?.defaultVideoIntegration,
                integrations: userData?.integrations?.map((integration) => ({ type: integration?.type })) || [],
                workspaceIds: user.workspaceIds,
                countryCode: userData?.countryCode,
                phoneNumber: userData?.phoneNumber,
            };
        });
        const currentUser = users?.find((user) => user.id === profile.userId);
        // If no workspaceId is provided, default to the first user workspace
        const savedWorkspaceId = helpers_1.Storage.Local.get(helpers_1.Storage.Key.SCHEDULER_WORKSPACE_ID);
        const workspaces = workspacesResponse.workspaces?.filter((workspace) => workspace?.isActive) || [];
        const currentUserActiveWorkspaces = currentUser?.workspaceIds?.filter((id) => workspaces.some((workspace) => workspace.id === id));
        const workspaceId = savedWorkspaceId && currentUserActiveWorkspaces?.find((id) => id === savedWorkspaceId)
            ? savedWorkspaceId
            : currentUserActiveWorkspaces?.[0] || '';
        const schedulerData = {
            userData: profile,
            role: roleResponse.role,
            workspaces,
            users,
            workspaceId,
            license: licensesResponse.licenses[0],
        };
        yield (0, effects_1.put)(actions_1.SUMO1SchedulerActions.getSchedulerDataSuccess(schedulerData));
        yield (0, effects_1.put)(actions_1.SUMO1SchedulerActions.getWorkspaceDataRequest());
        if (currentUser?.email) {
            analyticsService_1.analyticsService.identify(currentUser.email);
        }
        analyticsService_1.analyticsService.trackDialogOpened();
        const defaultHost = yield (0, effects_1.select)(global_1.globalSelectors.selectDefaultHost);
        // preselect current user as host
        if (currentUser && defaultHost === "ME" /* DefaultHost.ME */) {
            yield (0, effects_1.put)(actions_1.SUMO1SchedulerActions.setHost(currentUser));
        }
    }
    catch (error) {
        yield (0, effects_1.call)(reduxUtils_1.handleServiceError, error, constants_1.TOAST_ERROR_ID.SCHEDULER_USER_DOES_NOT_HAVE_NECESSARY_PERMISSIONS);
        yield (0, effects_1.put)(actions_1.SUMO1SchedulerActions.getSchedulerDataFailure(error));
    }
}
function* getWorkspaceDataSaga(action) {
    if (action.type !== actions_1.SUMO1SchedulerActionTypes.GET_WORKSPACE_DATA_REQUEST)
        return;
    try {
        yield (0, effects_1.put)(actions_1.SUMO1SchedulerActions.getMembersRequestAction());
        const workspaceId = yield (0, effects_1.select)(selectors_1.schedulerSelectors.selectWorkspaceId);
        const hasLocationsAccess = yield (0, effects_1.select)(selectors_1.schedulerSelectors.selectHasLocationsAccess);
        if (workspaceId !== helpers_1.Storage.Local.get(helpers_1.Storage.Key.SCHEDULER_WORKSPACE_ID)) {
            helpers_1.Storage.Local.set(helpers_1.Storage.Key.SCHEDULER_WORKSPACE_ID, workspaceId);
        }
        const requestsList = [
            (0, effects_1.call)(server_1.SumoServer.getMeetingTypes, { workspaceId }),
            ...(hasLocationsAccess ? [(0, effects_1.call)(server_1.SumoServer.getLocations, {})] : []),
        ];
        const [meetingTypesResponse, locationsResponse] = yield (0, effects_1.all)(requestsList);
        const workspaceData = {
            meetingTypes: meetingTypesResponse.meetingTypes,
            locations: locationsResponse?.locations || [],
        };
        yield (0, effects_1.put)(actions_1.SUMO1SchedulerActions.getWorkspaceDataSuccess(workspaceData));
        // switch to custom address if no locations available
        if (!workspaceData.locations.length) {
            yield (0, effects_1.put)(actions_1.SUMO1SchedulerActions.updateBookingPageWhere({
                inPersonType: constants_1.InPersonType.CUSTOM_ADDRESS,
                inPersonTypes: [constants_1.InPersonType.CUSTOM_ADDRESS],
            }));
        }
    }
    catch (error) {
        yield (0, effects_1.put)(actions_1.SUMO1SchedulerActions.getWorkspaceDataFailure(error));
    }
}
function* setHostSaga(action) {
    if (action.type !== actions_1.SUMO1SchedulerActionTypes.SET_HOST)
        return;
    try {
        const host = yield (0, effects_1.select)(selectors_1.schedulerSelectors.selectHost);
        if (host) {
            const hostDefaultVideoIntegration = yield (0, effects_1.select)(selectors_1.schedulerSelectors.selectHostDefaultVideoIntegration);
            yield (0, effects_1.put)(actions_1.SUMO1SchedulerActions.updateBookingPageWhere({
                videoConferenceType: hostDefaultVideoIntegration,
            }));
            yield (0, effects_1.put)(actions_1.SUMO1SchedulerActions.getHostScheduleRequest());
        }
        else {
            yield (0, effects_1.put)(actions_1.SUMO1SchedulerActions.getHostScheduleSuccess(null));
        }
    }
    catch (error) {
        yield (0, effects_1.put)(actions_1.SUMO1SchedulerActions.getHostScheduleFailure(error));
    }
}
function* getHostScheduleSaga(action) {
    if (action.type !== actions_1.SUMO1SchedulerActionTypes.GET_HOST_SCHEDULE_REQUEST)
        return;
    try {
        const host = yield (0, effects_1.select)(selectors_1.schedulerSelectors.selectHost);
        if (host) {
            const request = yield (0, effects_1.select)(selectors_1.schedulerSelectors.selectHostScheduleRequest);
            const response = yield (0, effects_1.call)(server_1.SumoServer.getHostSchedule, request);
            yield (0, effects_1.put)(actions_1.SUMO1SchedulerActions.getHostScheduleSuccess(response));
        }
        else {
            yield (0, effects_1.put)(actions_1.SUMO1SchedulerActions.getHostScheduleSuccess(null));
        }
    }
    catch (error) {
        yield (0, effects_1.put)(actions_1.SUMO1SchedulerActions.getHostScheduleFailure(error));
    }
}
function* upsertInboxBookingPageSaga(action) {
    if (action.type !== actions_1.SUMO1SchedulerActionTypes.SCHEDULER_UPSERT_INBOX_MEETING_TYPE_REQUEST)
        return;
    try {
        const request = yield (0, effects_1.select)(selectors_1.schedulerSelectors.selectInboxMeetingTypeRequest);
        const response = yield (0, effects_1.call)(server_1.SumoServer.schedulerUpsertInboxBookingPage, request);
        yield (0, effects_1.put)(actions_1.SUMO1SchedulerActions.schedulerUpsertInboxMeetingTypeSuccess(response));
        // insert title, guests and link to email
        const { composeViewId } = yield (0, effects_1.select)(selectors_1.schedulerSelectors.selectSchedulerParams);
        const upsertAction = yield (0, effects_1.select)(selectors_1.schedulerSelectors.selectUpsertAction);
        const subject = yield (0, effects_1.select)(selectors_1.schedulerSelectors.selectCustomName);
        const mainGuest = yield (0, effects_1.select)(selectors_1.schedulerSelectors.selectMainGuest);
        const guests = yield (0, effects_1.select)(selectors_1.schedulerSelectors.selectGuests);
        const bookingPageId = response.meetingType?.id;
        const shortLink = response.meetingType?.shortLink;
        const spots = response.spots;
        const duration = response.meetingType?.when?.duration;
        const sendGuests = [...(mainGuest ? [mainGuest] : []), ...guests].filter((member) => member?.email);
        const guestsPayload = {
            guests: sendGuests,
            subject,
            composeViewId,
        };
        yield (0, effects_1.call)(helpers_1.ExtensionRuntime.sendMessage, {
            target: constants_1.MessageTarget.SCHEDULER_DIALOG,
            type: constants_1.RegularMessage.SCHEDULER_INSERT_GUESTS_AND_SUBJECT,
            payload: guestsPayload,
        });
        analyticsService_1.analyticsService.trackGuestsInserted();
        if (upsertAction === constants_1.UpsertAction.COPY && shortLink) {
            const payload = { shortLink, composeViewId };
            yield (0, effects_1.call)(helpers_1.ExtensionRuntime.sendMessage, {
                target: constants_1.MessageTarget.SCHEDULER_DIALOG,
                type: constants_1.RegularMessage.SCHEDULER_COPY_LINK,
                payload,
            });
            analyticsService_1.analyticsService.trackLinkCopied();
        }
        else if (upsertAction === constants_1.UpsertAction.INSERT && spots && duration && bookingPageId) {
            const payload = {
                guests,
                spots,
                duration: { count: duration.count || 0, timeUnit: duration.timeUnit === constants_1.TimeUnit.HOUR ? constants_1.TimeUnit.HOUR : constants_1.TimeUnit.MINUTE },
                bookingPageId,
                composeViewId,
                spotUrl: (0, utils_1.createLink)({ bookingPageId }),
                querySpotKey: 'spot',
                querySpotFormat: 'number',
            };
            yield (0, effects_1.call)(helpers_1.ExtensionRuntime.sendMessage, {
                target: constants_1.MessageTarget.SCHEDULER_DIALOG,
                type: constants_1.RegularMessage.SCHEDULER_INSERT_SPOTS,
                payload,
            });
            analyticsService_1.analyticsService.trackSpotsInserted();
        }
    }
    catch (error) {
        if (error instanceof Error) {
            yield (0, effects_1.put)(actions_1.SUMO1SchedulerActions.schedulerUpsertInboxMeetingTypeFailure(error.message));
        }
        yield (0, effects_1.call)(reduxUtils_1.handleServiceError, error, constants_1.TOAST_ERROR_ID.SCHEDULER_CREATE_BOOKING_PAGE_ERROR);
    }
}
function* bookPageSaga(action) {
    if (action.type !== actions_1.SUMO1SchedulerActionTypes.SCHEDULER_BOOK_INSTANT_MEETING_REQUEST)
        return;
    try {
        const bookAction = yield (0, effects_1.select)(selectors_1.schedulerSelectors.selectBookAction);
        const payload = yield (0, effects_1.select)(selectors_1.schedulerSelectors.selectCreateInstantEventPayload);
        yield (0, effects_1.call)(server_1.SumoServer.createInstantEvent, payload);
        yield (0, effects_1.put)(actions_1.SUMO1SchedulerActions.schedulerBookInstantMeetingSuccess());
        if (bookAction === constants_1.BookAction.BOOK_CLOSED) {
            yield (0, effects_1.put)(actions_1.SUMO1SchedulerActions.setBookInstantMeetingSuccess());
            yield (0, effects_1.delay)(constants_1.TIME_DELAY_CLOSE_DIALOG);
            yield (0, effects_1.call)(helpers_1.ExtensionRuntime.sendMessage, { target: constants_1.MessageTarget.SCHEDULER_DIALOG, type: constants_1.RegularMessage.SCHEDULER_DIALOG_CLOSE });
            analyticsService_1.analyticsService.trackDialogClosed();
        }
        else {
            yield (0, effects_1.put)(actions_1.SUMO1SchedulerActions.getHostScheduleRequest());
            yield (0, effects_1.put)(actions_1.SUMO1SchedulerActions.setAvailabilityCustom([]));
            yield (0, effects_1.put)(notifications_1.notificationsActions.showToast({ id: constants_1.TOAST_ERROR_ID.SCHEDULER_BOOKED_SUCCESS_MESSAGE }));
        }
    }
    catch (error) {
        if (error instanceof Error) {
            yield (0, effects_1.put)(actions_1.SUMO1SchedulerActions.schedulerBookInstantMeetingFailure(error.message));
        }
        yield (0, effects_1.call)(reduxUtils_1.handleServiceError, error, constants_1.TOAST_ERROR_ID.SCHEDULER_CREATE_BOOKING_PAGE_ERROR);
    }
}
function* getMembersRequestSaga(action) {
    if (action.type !== actions_1.SUMO1SchedulerActionTypes.GET_MEMBERS_REQUEST)
        return;
    try {
        yield (0, effects_1.put)(actions_1.SUMO1SchedulerActions.clearMembersAction());
        // Step 1: Initialize CUSTOM guests from Gmail recipients FIRST
        const schedulerParams = yield (0, effects_1.select)(selectors_1.schedulerSelectors.selectSchedulerParams);
        if (schedulerParams.recipientsEmails) {
            const { mainGuest, guests } = (0, sagaUtils_1.createGuestsFromRecipients)(schedulerParams.recipientsEmails);
            // Set CUSTOM contacts as initial mainGuest/guests
            if (mainGuest) {
                yield (0, effects_1.put)(actions_1.SUMO1SchedulerActions.setMainGuest(mainGuest));
            }
            if (guests.length > 0) {
                yield (0, effects_1.put)(actions_1.SUMO1SchedulerActions.setGuests(guests));
            }
            // Add CUSTOM contacts to membersData
            const allCustomMembers = [...(mainGuest ? [mainGuest] : []), ...guests];
            yield (0, effects_1.put)(actions_1.SUMO1SchedulerActions.addMembersAction(allCustomMembers));
        }
        const workspaceId = yield (0, effects_1.select)(selectors_1.schedulerSelectors.selectWorkspaceId);
        const integrations = [
            constants_1.IntegrationType.GOOGLE_CONTACTS,
            constants_1.IntegrationType.MICROSOFT_CONTACTS,
            "SUMO1" /* WorkspaceIntegrationType.SUMO1 */,
            "SALESFORCE" /* WorkspaceIntegrationType.SALESFORCE */,
            "SALESFORCE_CEP" /* WorkspaceIntegrationType.SALESFORCE_CEP */,
            "HUBSPOT" /* WorkspaceIntegrationType.HUBSPOT */,
            "MS_DYNAMICS" /* WorkspaceIntegrationType.MS_DYNAMICS */,
        ];
        // Step 2: Fetch integration members in parallel
        yield (0, effects_1.all)(integrations.map((integrationType) => (0, effects_1.call)(function* () {
            let page = 1;
            while (true) {
                const response = yield (0, effects_1.call)(server_1.SumoServer.getSchedulerMembers, {
                    workspaceId,
                    integrationType,
                    page,
                });
                const members = (0, sagaUtils_1.addSubtypeToMembers)(response.members);
                yield (0, effects_1.put)(actions_1.SUMO1SchedulerActions.addMembersAction(members));
                if (page < (response.numberOfPages || 0)) {
                    page++;
                }
                else {
                    break;
                }
            }
        })));
        yield (0, effects_1.put)(actions_1.SUMO1SchedulerActions.getMembersSuccessAction());
    }
    catch (error) {
        yield (0, effects_1.put)(actions_1.SUMO1SchedulerActions.getMembersFailAction(error));
    }
}
function* watchSUMO1SchedulerSaga() {
    yield (0, effects_1.takeLatest)(actions_1.SUMO1SchedulerActionTypes.GET_SCHEDULER_DATA_REQUEST, getSchedulerDataSaga);
    yield (0, effects_1.takeLatest)(actions_1.SUMO1SchedulerActionTypes.GET_WORKSPACE_DATA_REQUEST, getWorkspaceDataSaga);
    yield (0, effects_1.takeLatest)(actions_1.SUMO1SchedulerActionTypes.SET_HOST, setHostSaga);
    yield (0, effects_1.takeLatest)(actions_1.SUMO1SchedulerActionTypes.GET_HOST_SCHEDULE_REQUEST, getHostScheduleSaga);
    yield (0, effects_1.takeLatest)(actions_1.SUMO1SchedulerActionTypes.SCHEDULER_UPSERT_INBOX_MEETING_TYPE_REQUEST, upsertInboxBookingPageSaga);
    yield (0, effects_1.takeLatest)(actions_1.SUMO1SchedulerActionTypes.SCHEDULER_BOOK_INSTANT_MEETING_REQUEST, bookPageSaga);
    yield (0, effects_1.takeLatest)(actions_1.SUMO1SchedulerActionTypes.GET_MEMBERS_REQUEST, getMembersRequestSaga);
}
exports.watchSUMO1SchedulerSaga = watchSUMO1SchedulerSaga;
